import { FileTextOutlined, BarChartOutlined, UnorderedListOutlined, AppstoreOutlined } from "@ant-design/icons";
import { Button, Card, Col, Row, Typography, Empty, Progress, Statistic, Skeleton, Spin } from "antd";
import { useRef, useState, useMemo, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const { Text, Title } = Typography;

const FormList = ({ formsData, totalAnswers, dateRange, loading, locationId, topicId, includeChildren, user, onFormClick, onOrderingChange }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [visibleItems, setVisibleItems] = useState(12); // Começar com 12 itens
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' ou 'list'
  const [orderingType, setOrderingType] = useState('insertDate'); // 'insertDate', 'updateDate' ou 'alphabetical'
  const itemsPerLoad = 12; // Carregar 12 itens por vez

  // Memoizar dados visíveis para otimizar renderização
  const visibleData = useMemo(() => {
    if (!formsData || formsData.length === 0) return [];
    return formsData.slice(0, visibleItems);
  }, [formsData, visibleItems]);

  const hasMore = formsData && formsData.length > visibleItems;

  // Função para carregar mais itens
  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);
    setTimeout(() => {
      setVisibleItems(prev => Math.min(prev + itemsPerLoad, formsData.length));
      setIsLoadingMore(false);
    }, 300); // Simular carregamento
  }, [isLoadingMore, hasMore, itemsPerLoad, formsData?.length]);

  // Função para mudar ordenação
  const handleOrderingChange = (type) => {
    setOrderingType(type);
    // Chama o callback para notificar o componente pai sobre a mudança
    if (onOrderingChange) {
      // 'insertDate' = Mais Respondidos (por quantidade)
      // 'updateDate' = Últimos Respondidos (por data da última resposta)
      // 'alphabetical' = Ordenação Alfabética (por nome do formulário)
      const orderField = type === 'updateDate' ? 'LastAnsweredDate' :
        type === 'alphabetical' ? 'FormName' : 'AnswerCount';
      onOrderingChange(orderField);
    }
  };

  // Observer para detectar quando chegar no final
  const observerRef = useCallback((node) => {
    if (isLoadingMore) return;
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        loadMore();
      }
    });

    if (node) observer.current.observe(node);
  }, [isLoadingMore, hasMore, loadMore]);

  const observer = useRef();

  const handleFormClick = (form) => {
    // Call the parent callback to switch to list view
    if (onFormClick) {
      onFormClick(form);
    }
  };

  if (loading) {
    return (
      <div style={{ marginTop: 24 }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          marginBottom: 16,
          padding: "0 4px"
        }}>
          <BarChartOutlined style={{ fontSize: 18, color: "#4085c6", marginRight: 8 }} />
          <Title level={4} style={{ margin: 0, color: "#1a1a1a", fontWeight: 600 }}>
            {t("Reports.FormsList")}
          </Title>
        </div>
        <Row gutter={[8, 8]}>
          {[1, 2, 3, 4].map((i) => (
            <Col xs={24} sm={12} md={8} lg={6} xl={6} key={i}>
              <Card style={{ borderRadius: 12, height: 200 }}>
                <Skeleton active paragraph={{ rows: 3 }} />
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    );
  }

  if (!formsData || formsData.length === 0) {
    return (
      <div style={{ marginTop: 24 }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          marginBottom: 16,
          padding: "0 4px"
        }}>
          <BarChartOutlined style={{ fontSize: 18, color: "#4085c6", marginRight: 8 }} />
          <Title level={4} style={{ margin: 0, color: "#1a1a1a", fontWeight: 600 }}>
            {t("Reports.FormsList")}
          </Title>
        </div>
        <Card style={{ borderRadius: 8 }}>
          <Empty
            description={t("Reports.NoFormsFound")}
            style={{ margin: "40px 0" }}
          />
        </Card>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 24 }}>
      <div style={{
        display: "flex",
        alignItems: "center",
        marginBottom: 8,
        padding: "0 4px",
        gap: 16
      }}>
        <BarChartOutlined style={{ fontSize: 18, color: "#4085c6" }} />
        <Title level={4} style={{ margin: 0, color: "#1a1a1a", fontWeight: 600 }}>
          {t("Reports.FormsList")}
        </Title>
      </div>

      <Text type="secondary" style={{ fontSize: '13px', marginBottom: 8, marginTop: -4, display: 'block', padding: "0 4px" }}>
        {t('Reports.InfoTextStart')} <span style={{
          color: "#fff",
          fontWeight: 600,
          backgroundColor: "#4085c6",
          padding: "1px 8px",
          borderRadius: "3px",
          border: "1px solid #4085c6"
        }}>{t('Reports.CompletedTasks')}</span> {t('Reports.InfoTextEnd')} {t('Reports.ClickToNavigate')}
      </Text>

      {/* Grupos de botões embaixo do título */}
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Text type="secondary" style={{ fontSize: '13px', display: 'block', padding: "0 4px" }}>
            {t('Reports.ViewMode')}:
          </Text>
          <Button.Group size="small">
            <Button
              type={viewMode === 'cards' ? 'primary' : 'default'}
              icon={<AppstoreOutlined />}
              onClick={() => setViewMode('cards')}
            >
              {t('Reports.Cards')}
            </Button>
            <Button
              type={viewMode === 'list' ? 'primary' : 'default'}
              icon={<UnorderedListOutlined />}
              onClick={() => setViewMode('list')}
            >
              {t('Reports.List')}
            </Button>
          </Button.Group>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Text type="secondary" style={{ fontSize: '13px', display: 'block', padding: "0 4px" }}>
            {t('Reports.OrderBy')}:
          </Text>
          <Button.Group>
            <Button
              type={orderingType === 'insertDate' ? 'primary' : 'default'}
              onClick={() => handleOrderingChange('insertDate')}
              size="small"
            >
              {t('Reports.OrderByInsert')}
            </Button>
            <Button
              type={orderingType === 'updateDate' ? 'primary' : 'default'}
              onClick={() => handleOrderingChange('updateDate')}
              size="small"
            >
              {t('Reports.OrderByUpdate')}
            </Button>
            <Button
              type={orderingType === 'alphabetical' ? 'primary' : 'default'}
              onClick={() => handleOrderingChange('alphabetical')}
              size="small"
            >
              {t('Reports.OrderByAlphabetical')}
            </Button>
          </Button.Group>
        </div>
      </div>

      {viewMode === 'cards' ? (
        <Row gutter={[8, 8]}>
          {visibleData.map((form, index) => {
            const percentage = totalAnswers > 0 ? (form.answerCount / totalAnswers) * 100 : 0;

            return (
              <Col xs={24} sm={12} md={8} lg={6} xl={6} key={form.formId || index}>
                <Card
                  className="form-list-item"
                  style={{
                    height: "100%",
                    border: "1px solid #f0f0f0",
                    borderRadius: 12,
                    transition: "all 0.2s ease",
                    backgroundColor: "#fff",
                    cursor: "pointer",
                  }}
                  bodyStyle={{ padding: 20 }}
                  onClick={() => handleFormClick(form)}
                >
                  {/* Header com ícone e título */}
                  <div style={{
                    display: "flex",
                    alignItems: "flex-start",
                    marginBottom: 16,
                    gap: 12
                  }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 40,
                        height: 40,
                        backgroundColor: "#f8f9fa",
                        borderRadius: 8,
                        border: "2px solid #e9ecef",
                        flexShrink: 0
                      }}
                    >
                      <FileTextOutlined style={{ fontSize: 20, color: "#4085c6" }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Title
                        level={5}
                        style={{
                          margin: 0,
                          marginBottom: 4,
                          color: "#1a1a1a",
                          fontWeight: 600,
                          fontSize: 16,
                          lineHeight: 1.3,
                        }}
                        ellipsis={{ tooltip: form.formName, rows: 2 }}
                      >
                        {form.formName}
                      </Title>

                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                        <Text style={{ fontSize: 11, color: "#666", fontWeight: 500 }}>
                          {t('Reports.Version')} {form.version || 'N/A'}
                        </Text>
                      </div>
                    </div>
                  </div>

                  {/* Estatísticas */}
                  <div style={{ marginBottom: 16 }}>
                    <Row gutter={8}>
                      <Col span={12}>
                        <Statistic
                          title={t('Reports.Answers')}
                          value={form.answerCount}
                          valueStyle={{
                            fontSize: 20,
                            fontWeight: 700,
                            color: "#4085c6"
                          }}
                          suffix=""
                        />
                      </Col>
                      <Col span={12}>
                        <Statistic
                          title={t('Reports.Percentage')}
                          value={percentage.toFixed(1)}
                          valueStyle={{
                            fontSize: 20,
                            fontWeight: 700,
                            color: "#34d399"
                          }}
                          suffix="%"
                        />
                      </Col>
                    </Row>
                  </div>

                  {/* Barra de progresso */}
                  <div>
                    <Progress
                      percent={parseFloat(percentage.toFixed(2))}
                      size="small"
                      strokeColor={{
                        '0%': '#4085c6',
                        '100%': '#60a5fa',
                      }}
                      style={{ marginBottom: 4 }}
                    />
                    <Text style={{ fontSize: 11, color: "#999" }}>
                      {percentage.toFixed(1)}% {t('Reports.ofTotalAnswers')}
                    </Text>
                  </div>

                </Card>
              </Col>
            );
          })}
        </Row>
      ) : (
        <div>
          {visibleData.map((form, index) => {
            const percentage = totalAnswers > 0 ? (form.answerCount / totalAnswers) * 100 : 0;

            return (
              <div
                key={form.formId || index}
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "20px 24px",
                  marginBottom: 12,
                  backgroundColor: "#fff",
                  borderRadius: 12,
                  cursor: "pointer",
                  transition: "all 0.3s ease"
                }}
                onClick={() => handleFormClick(form)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 48,
                    height: 48,
                    backgroundColor: "#f8f9fa",
                    borderRadius: 10,
                    border: "2px solid #e9ecef",
                    flexShrink: 0,
                    marginRight: 20
                  }}
                >
                  <FileTextOutlined style={{ fontSize: 20, color: "#4085c6" }} />
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
                    <Title level={4} style={{ margin: 0, fontSize: 16, fontWeight: 600, color: "#1a1a1a" }}>
                      {form.formName}
                    </Title>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {t('Reports.Version')}: {form.version}
                    </Text>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{
                        width: 8,
                        height: 8,
                        backgroundColor: "#4085c6",
                        borderRadius: "50%"
                      }} />
                      <Text style={{ fontSize: 14, color: "#666" }}>
                        {form.answerCount} {t('Reports.Answers')}
                      </Text>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{
                        width: 8,
                        height: 8,
                        backgroundColor: "#52c41a",
                        borderRadius: "50%"
                      }} />
                      <Text style={{ fontSize: 14, color: "#666" }}>
                        {percentage.toFixed(1)}% {t('Reports.ofTotalAnswers')}
                      </Text>
                    </div>
                  </div>
                </div>

                <div style={{ textAlign: 'right', flexShrink: 0, minWidth: 120 }}>
                  <div style={{
                    fontSize: 24,
                    fontWeight: 700,
                    color: "#4085c6",
                    marginBottom: 8,
                    lineHeight: 1
                  }}>
                    {form.answerCount}
                  </div>
                  <Progress
                    percent={percentage}
                    size="small"
                    strokeColor="#4085c6"
                    style={{ width: 100 }}
                    showInfo={false}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Scroll infinito trigger */}
      {hasMore && (
        <div ref={observerRef} style={{ marginTop: 24, textAlign: 'center', padding: '20px 0' }}>
          {isLoadingMore ? (
            <Spin size="large" />
          ) : (
            <Text type="secondary">
              {t('Reports.ScrollToLoadMore', 'Role para carregar mais formulários')}
            </Text>
          )}
        </div>
      )}

      {/* Contador de itens */}
      {formsData && formsData.length > 0 && (
        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {t('Reports.ShowingItems', 'Mostrando {{visible}} de {{total}} formulários', {
              visible: visibleItems,
              total: formsData.length
            })}
          </Text>
        </div>
      )}
    </div>
  );
};

export default FormList;